import React, { useEffect, useState } from "react";
import CloudStorage from "../../../CloudStorage/CloudStorage";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { UpdateTask } from "../../../redux/team/action";
import ReactSelect from "react-select";
import CustomReactQuillDescription from "../../Common/CustomeLabel/ReactQuill/CustomReactQuillDescription";
import downloadFiles from "../../Common/FilesRelevent/DownloadFiles";
import GetRemainOnlyDays from "../MyOrders/MyOrders/PostedOrders/PostedProjects/MineActiveProject/ProjectModels/GetRemainOnlyDays";
import DisplayFileExtensionWise from "../../Common/FilesRelevent/DisplayFileExtensionWise";
import ProfileImageWithFallback from "../../../common/ProfileImageWithFallback";
import AsteriskLabel from "../../Common/AsteriskLabel";
import TimePickerRSuite from "../../Common/TimePickerRSuite";

const statusOptions = [
    { value: "1", label: "Assigned" },
    { value: "2", label: "In Progress" },
    { value: "3", label: "Completed" },
    { value: "4", label: "Reopened" },
];

export default function ModalMilestoneDetails({ data }) {
    const [loading, setLoading] = useState(false);
    const [statusIndex, setStatusIndex] = useState(null);
    const dispatch = useDispatch();

    const currentDate = new Date().toISOString().split("T")[0];

    // ✅ Yup validation for due_time
    const validationSchema = Yup.object().shape({
        due_date: Yup.date().required("Due date is required"),
        due_time: Yup.string()
            .required("Due time is required")
            .test("not-past-time", "Time cannot be in the past", function (value) {
                const { due_date } = this.parent;
                if (!value || !due_date) return true;

                const [hours, minutes] = value.split(":").map(Number);
                const selectedDateTime = new Date(due_date);
                selectedDateTime.setHours(hours, minutes, 0, 0);

                const now = new Date();

                // Only compare if selected date is today
                if (
                    selectedDateTime.toDateString() === now.toDateString() &&
                    selectedDateTime < now
                ) {
                    return false;
                }

                return true;
            }),
    });

    const formik = useFormik({
        initialValues: {
            due_date: data?.due_date || "",
            due_time: data?.due_time || "",
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: (values) => {
            const payload = {
                type: 5,
                id: data?.model_id,
                due_date: values.due_date,
                due_time: values.due_time,
                reload: true,
            };
            dispatch(UpdateTask(payload));
        },
    });

    // ✅ Handle status change
    const changeStatus = (status) => {
        setLoading(true);
        const payload = {
            status,
            type: 3,
            id: data?.model_id,
            reload: true,
        };
        dispatch(UpdateTask(payload));
        setStatusIndex(statusOptions.find((item) => item.value === status));
        setLoading(false);
    };

    // ✅ Handle date change
    const handleDateChange = (event) => {
        const newDate = event.target.value;
        formik.setFieldValue("due_date", newDate);
        const payload = {
            type: 5,
            id: data?.model_id,
            due_date: newDate,
            due_time: formik.values.due_time,
            reload: true,
        };
        dispatch(UpdateTask(payload));
    };

    // ✅ Automatically update backend when time changes (Formik watches the field)
    useEffect(() => {
        if (!formik.values.due_time) return;

        // Skip dispatch on initial mount (only after actual user change)
        if (data?.due_time === formik.values.due_time) return;

        formik.validateField("due_time").then((error) => {
            if (!error) {
                const payload = {
                    type: 5,
                    id: data?.model_id,
                    due_date: formik.values.due_date,
                    due_time: formik.values.due_time,
                    reload: true,

                };
                dispatch(UpdateTask(payload));
            }
        });
    }, [formik.values.due_time]); // 👈 triggers only when user selects time

    // ✅ Set initial dropdown
    useEffect(() => {
        if (data?.status) {
            setStatusIndex(statusOptions.find((item) => item.value == data?.status));
        }
    }, [data]);

    return (
        <div className="modal-content">
            <div className="modal-body-second modal-body-title-second">
                <h6>Task Details</h6>
                <h2>{data?.post_title}</h2>

                {/* ========== Assignee Section ========== */}
                <div className="assignee">
                    <h3 className="py-3">Assignee </h3>
                    <div className="assignee-sec">
                        <h4
                            className="d-flex align-items-center"
                            style={{ columnGap: "15px" }}
                        >
                            <ProfileImageWithFallback
                                url={data.profile_image}
                                style={{ width: 40, height: 40, borderRadius: 20 }}
                            />
                            {data?.first_name} {data?.last_name}
                        </h4>

                        {/* Status */}
                        <ReactSelect
                            options={statusOptions}
                            placeholder={"Select status"}
                            value={statusIndex}
                            onChange={(options) => {
                                setStatusIndex(options);
                                changeStatus(options?.value);
                            }}
                        />
                    </div>

                    {/* Deadline */}
                    <div className="assignee pt-2 pb-3">
                        <label>Deadline</label>
                        <div className="assignee-sec assignee-bottom ">
                            <div className="form-group mb-0">
                                <input
                                    type="date"
                                    name="due_date"
                                    value={formik.values.due_date}
                                    onChange={handleDateChange}
                                    placeholder="Select a date"
                                    min={currentDate}
                                    className="form-control"
                                />
                                {formik.touched.due_date && formik.errors.due_date && (
                                    <div style={{ color: "red" }}>{formik.errors.due_date}</div>
                                )}
                            </div>
                            <GetRemainOnlyDays enddate={data?.due_date} />
                        </div>
                    </div>

                    {/* Due Time */}
                    <div className="form-group col-md-6">
                        <label>
                            Due Time <AsteriskLabel />
                        </label>
                        <TimePickerRSuite
                            formik={formik}
                            name="due_time"
                            label=""
                        />
                        <br />
                        {formik.touched.due_time && formik.errors.due_time && (
                            <div style={{ color: "red" }}>{formik.errors.due_time}</div>
                        )}
                    </div>
                </div>

                {/* Description */}
                <h6>Description</h6>
                <CustomReactQuillDescription value={data?.description} />

                {/* Files */}
                {Array.isArray(data?.files) && data.files?.length > 0 && (
                    <>
                        <h6>Attachments</h6>
                        {data.files.map((item, index) => (
                            <div
                                key={index}
                                className="attachment-team"
                                style={{ position: "relative" }}
                            >
                                <div className="attachment-left">
                                    <DisplayFileExtensionWise
                                        item={item}
                                        className="w-100 attach-img"
                                    />
                                </div>
                                <button
                                    style={{ position: "absolute", top: "10px", right: "10px" }}
                                    onClick={() => downloadFiles(item?.file)}
                                    className="btn"
                                >
                                    <img
                                        src={`${CloudStorage.cloudurl}/Assets/Images/common/download-icon-black.png`}
                                        alt=""
                                    />
                                </button>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}
